import { Component, forwardRef, Input, OnInit } from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { ScrollDispatcher } from '@angular/cdk/overlay'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'

import { BehaviorSubject, Observable } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators'

import { DestroyService } from '@core/services/destroy.service'

@Component({
  selector: 'lin-table-autocomplete-select',
  templateUrl: './table-autocomplete-select.component.html',
  styleUrls: ['./table-autocomplete-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TableAutocompleteSelectComponent),
      multi: true,
    },
    DestroyService,
  ],
  standalone: false,
})
export class TableAutocompleteSelectComponent implements ControlValueAccessor, OnInit {
  @Input() autocompleteRequest: (page?: number, pageSize?: number, sort?: string, filter?: string, fields?: unknown[]) => Observable<any>
  @Input() displayWith: (value: any) => string
  @Input() valueField: string
  @Input() pageSize = 30

  control = new FormControl()
  isAutocompleteLoading = false
  autocompleteOptions = new BehaviorSubject<unknown[]>([])
  height = 200

  private total: number
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange = (value: string) => {}
  public _fetchedPages = new Set<number>()
  private page = 0
  private isLoading = new BehaviorSubject<boolean>(false)
  isLoading$ = this.isLoading.asObservable()

  get options(): unknown[] {
    return this.autocompleteOptions.getValue()
  }

  constructor(
    public scrollDispatcher: ScrollDispatcher,
    private readonly destroyService: DestroyService,
  ) {}

  ngOnInit(): void {
    this.initControlHandler()
  }

  scrollHandler(): void {
    this.fetchData()
    this.isLoading.next(true)

    if (this.total > this.options.length) {
      this.isLoading.next(true)
    } else {
      this.isLoading.next(false)
    }
  }

  private clearList(val = ''): void {
    this.autocompleteOptions.next([])
    this.page = 0
    this.total = null
    this.isAutocompleteLoading = true
    this.fetchData(undefined, val)
  }

  initControlHandler(): void {
    this.control.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => (this.isAutocompleteLoading = true)),
        takeUntil(this.destroyService),
      )
      .subscribe((val: string) => {
        if (val?.length) {
          this.clearList(val)
        } else {
          this.clearList('')
        }
      })
  }

  fetchData(sort?: string, filter?: string, fields?: unknown[]): void {
    if (!this.autocompleteRequest) return
    if (this.total && this.page * this.pageSize >= this.total) return

    this.page = this.page + 1

    this.autocompleteRequest(this.page, this.pageSize, sort, filter, fields)
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.isLoading.next(true)),
        takeUntil(this.destroyService),
      )
      .subscribe(({ data, total }: { data: unknown[]; total: number }) => {
        const itemHeight = 48
        const maxViewportHeght = 200
        const viewportHeght = data?.length * itemHeight + itemHeight

        this.isLoading.next(false)
        this.total = total
        this.autocompleteOptions.next([...this.autocompleteOptions.getValue(), ...data])
        this.isAutocompleteLoading = false
        this.height = viewportHeght >= maxViewportHeght ? maxViewportHeght : viewportHeght
      })
  }

  writeValue(obj: any): void {
    this.control.setValue(obj)
  }

  registerOnChange(fn: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.onChange = fn
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  registerOnTouched(fn: any): void {}

  handleClose(event: MatAutocompleteSelectedEvent): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.onChange(event.option.value[this.valueField] as string)
  }
}
